import React, { useState, useEffect, useContext } from "react";
import { SEO, ISeoProps } from "../seo/seo";
import Header from "../header/header";
import Footer from "../footer/footer";
import SimpleFooter from "../footer/simple-footer";
import {
  Body,
  ColorsCss,
  Content,
  ContentBackground,
  FontsCss,
  GlobalCss,
  GridCss,
  HeaderBase,
  SectionsCss,
  SlickCarouselCss,
  ThemeCss,
  TypographyCss,
} from "./layout.styled";
import { Global } from "@emotion/react";
import Helmet from "react-helmet";
import PrivacyBanner from "../privacy-banner/privacy-banner";
import { AnnouncementBanner } from "../announcement-banner/announcement-banner";
import { IHeaderProps } from "../header/header";
import { ILocalesProps, Locales } from "../locales/locales";
import ThemeContext from "../../utils/contexts/theme-context";
import { useThemeContext } from "../../utils/contexts/theme-context";
import { UserValidationContext } from "../../utils/contexts/user-validation-context";
import { setPrechatFields } from "../../utils/salesforceChat";
import Announcement from "../modules/announcement";
import { PrismicRichTextField } from "../../types/graphql";

interface LayoutProps {
  children: React.ReactNode;
  seo: ISeoProps;
  jsonLd?: string | null | undefined;
  announcementContent?: PrismicRichTextField | null | undefined;
  isGuttered: boolean;
  headerLinksUID?: string;
  headerCTAsUID?: string;
  simpleFooter?: boolean;
  showAnnouncementBanner?: boolean;
  disableSearch?: boolean;
  locales?: ILocalesProps;
  bgGradient?: boolean;
  darkMode?: boolean;
  isStatsigLandingPage?: boolean;
  navExperimentEligible?: boolean;
}

// Returns props for header. The header is rendered based on
// the selected content entry's uid for the header links and header ctas
// content types. These uid's are passed in from the template level. If
// uids are not passed in from the template, this function is hardcoded
//to return the default content entry uids.
const getHeaderProps = (props: LayoutProps): IHeaderProps => ({
  headerLinksUID: props.headerLinksUID || "default-links",
  headerCTAsUID: props.headerCTAsUID || "default-ctas",
  disableSearch: props.disableSearch || false,
  showAnnouncementBanner: props.showAnnouncementBanner || false,
  isDarkMode: props.darkMode || false,
  navExperimentEligible: props.navExperimentEligible ?? true,
});

const BodyElement = (props: LayoutProps) => {
  const {
    seo,
    jsonLd,
    locales,
    children,
    simpleFooter,
    bgGradient,
    showAnnouncementBanner,
    announcementContent,
    darkMode,
    isStatsigLandingPage,
  } = props;
  const { currentTransitionPercent, setCurrentTransitionPercent } =
    useThemeContext();
  const [scrollPosition, setScrollPosition] = useState(0);
  const user = useContext(UserValidationContext);
  const isDarkMode = darkMode || false;

  const [headerHeight, setHeaderHeight] = useState(157);
  const [heroHeight, setHeroHeight] = useState(600);
  const [scrollFinal, setScrollFinal] = useState(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [backgroundOpacity, setBackgroundOpacity] = useState(1);

  // Handle visibility of Header
  const handleScroll = () => {
    let position = window.pageYOffset;
    setScrollPosition(position);
  };

  // OnScroll and onTouch event listener
  useEffect(() => {
    const onScroll = () => handleScroll();
    const onTouchEnd = () => handleScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [scrollPosition]);

  // Used to calculate visibility of header and dark mode
  useEffect(() => {
    setScrollPercent(scrollPosition / scrollFinal);
  }, [scrollPosition, scrollFinal]);

  useEffect(() => {
    setBackgroundOpacity(scrollPercent > 1 ? 0 : 1);
  }, [scrollPercent]);

  useEffect(() => {
    setCurrentTransitionPercent(backgroundOpacity);
  }, [backgroundOpacity]);

  // Retrieve height of header and hero for header dropdown top offset and dark mode visibility
  useEffect(() => {
    const header = document.getElementById("site-header");
    const hero = document.getElementById("home-hero");

    // Handler to call on window resize
    const handleResize = () => {
      setTimeout(() => {
        if (header?.offsetHeight) {
          setHeaderHeight(header?.offsetHeight);
        }
        if (hero?.offsetHeight) {
          setHeroHeight(hero?.offsetHeight);
        }
      }, 100);
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setScrollFinal(heroHeight);
  }, [headerHeight, heroHeight]);

  useEffect(() => {
    function callback() {
      if (user.isReady && user.userData && user.isValid && !user.isExpired) {
        // set prechat fields
        setPrechatFields({
          FirstName: user.userData.firstName,
          LastName: user.userData.lastName,
          UserEmail: user.userData.emailAddress,
        });
      } else {
        // unset fields
        setPrechatFields({
          FirstName: undefined,
          LastName: undefined,
          UserEmail: undefined,
        });
      }
    }

    callback();
    window.addEventListener("onEmbeddedMessagingReady", callback);
    return () =>
      window.removeEventListener("onEmbeddedMessagingReady", callback);
  }, [user]);

  return (
    <Body
      isDarkMode={isDarkMode}
      bgOpacity={currentTransitionPercent}
      scrollPosition={scrollPosition}
      bgGradient={bgGradient}
    >
      <Helmet>
        <noscript>The Xometry app works best with JavaScript enabled!</noscript>
      </Helmet>
      {!isStatsigLandingPage && (
        <>
          <Global styles={FontsCss} />
          <Global styles={GlobalCss} />
          <Global styles={GridCss} />
          <Global styles={SectionsCss} />
          <Global styles={TypographyCss} />
          <Global styles={ColorsCss} />
          <Global styles={ThemeCss} />
          <Global styles={SlickCarouselCss} />
          <SEO {...seo} />
          {jsonLd && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: jsonLd.replace(/</g, "\\u003c"),
              }}
            />
          )}
          {locales && <Locales {...locales} />}
          <HeaderBase
            id="site-header"
            isDarkMode={isDarkMode}
            bgOpacity={currentTransitionPercent}
            scrollPosition={scrollPosition}
          >
            {showAnnouncementBanner && announcementContent ? (
              <Announcement announcementContent={announcementContent} />
            ) : (
              showAnnouncementBanner && <AnnouncementBanner />
            )}
            <Header {...getHeaderProps(props)} />
          </HeaderBase>
          <ContentBackground>
            <Content {...props}>{children}</Content>
          </ContentBackground>
          {simpleFooter ? <SimpleFooter /> : <Footer />}
          <PrivacyBanner />
          {isDarkMode && (
            <div className="fixed top-0 left-0 right-0 h-full">
              {/* Background left */}
              <div className="absolute top-0 -left-[230%] sm:-left-[125%] md:-left-[100%] lg:-left-[80%] xl:-left-[43%] h-full">
                <svg
                  width="1272"
                  height="1655"
                  viewBox="0 0 1272 1655"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_f_374_334)">
                    <path
                      d="M971.828 745.011C1332.26 423.148 397.995 187.867 328.385 220.286C258.776 252.705 86.0449 402.311 396.025 1123.95C706.005 1845.59 521.285 1147.34 971.828 745.011Z"
                      fill="#0E6AED"
                      fill-opacity="0.9"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_f_374_334"
                      x="-0.000198364"
                      y="4.57764e-05"
                      width="1271.55"
                      height="1654.17"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        result="shape"
                      />
                      <feGaussianBlur
                        stdDeviation="108.65"
                        result="effect1_foregroundBlur_374_334"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
              {/* Background right */}
              <div className="absolute -top-[10%] -right-[240%] sm:-right-[125%] md:-right-[100%] lg:-right-[80%] xl:-right-[50%] h-full">
                <svg
                  width="1272"
                  height="1655"
                  viewBox="0 0 1272 1655"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_f_374_336)">
                    <path
                      d="M971.828 745.011C1332.26 423.148 397.995 187.867 328.386 220.286C258.777 252.705 86.0454 402.311 396.025 1123.95C706.005 1845.59 521.286 1147.34 971.828 745.011Z"
                      fill="#0E6AED"
                      fill-opacity="0.9"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_f_374_336"
                      x="0.000778198"
                      y="4.57764e-05"
                      width="1271.55"
                      height="1654.17"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        result="shape"
                      />
                      <feGaussianBlur
                        stdDeviation="108.65"
                        result="effect1_foregroundBlur_374_336"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>
          )}
        </>
      )}
    </Body>
  );
};

const Layout = (props: LayoutProps) => {
  const { darkMode } = props;
  return (
    <ThemeContext darkMode={darkMode}>
      <BodyElement {...props} />
    </ThemeContext>
  );
};

export default Layout;
